import { Platform } from "react-native";
import { Control, FieldPath, FieldValues } from "react-hook-form";

import {
  VnFormControl,
  VnFormDescription,
  VnFormErrorMessage,
  VnFormField,
  VnFormItem,
  VnFormLabel,
} from "@/components/VnForm";
import { OptionType } from "@/domain/common";
import { I18nKeyType } from "@/i18n";
import { cn } from "@/utils/tailwind";

import { VnBox } from "../VnBox";
import { TextVariantType } from "../VnText/variants";
import { VnTouchableOpacity } from "../VnTouchableOpacity";
import { BaseRadioGroup, BaseRadioGroupItem } from "./BaseRadioGroupInput";

type RadioOptionType = OptionType & {
  isDisabled?: boolean;
  descriptionI18nKey?: I18nKeyType;
  descriptionI18nOptions?: object;
};

type VnRadioGroupInputPropsType<T extends FieldValues> = {
  name: FieldPath<T>;
  control: Control<T>;
  options: RadioOptionType[];
  labelI18nKey?: I18nKeyType;
  labelI18nOptions?: object;
  descriptionI18nKey?: I18nKeyType;
  descriptionI18nOptions?: object;
  showError?: boolean;
  errorI18nKey?: I18nKeyType;
  errorI18nOptions?: object;
  isDisabled?: boolean;
  className?: string;
  radioGroupClassName?: string;
  radioItemLabelClassName?: string;
  radioItemLabelVariant?: TextVariantType;
  radioItemClassName?: string;
  isRequired?: boolean;
  variant?: "icon" | "card";
};

export const VnRadioGroupInput = <T extends FieldValues>({
  name,
  control,
  options,
  labelI18nKey,
  labelI18nOptions,
  descriptionI18nKey,
  descriptionI18nOptions,
  showError = true,
  errorI18nKey,
  errorI18nOptions,
  className,
  radioGroupClassName,
  radioItemLabelClassName,
  radioItemLabelVariant,
  radioItemClassName,
  isDisabled,
  isRequired,
}: VnRadioGroupInputPropsType<T>) => {
  return (
    <VnFormField
      name={name}
      control={control}
      render={({ field }) => (
        <VnFormItem>
          <VnBox className={cn("flex flex-col gap-2", className)}>
            {labelI18nKey && (
              <VnFormLabel
                i18nKey={labelI18nKey}
                i18nOptions={labelI18nOptions}
                isDisabled={isDisabled}
                isRequired={isRequired}
              />
            )}
            <VnFormControl>
              <BaseRadioGroup
                value={field.value}
                onValueChange={value => {
                  field.onChange(value);
                }}
                className={cn("flex flex-col gap-y-2", radioGroupClassName)}>
                {options.map(option => (
                  <VnFormItem key={option.value}>
                    <VnTouchableOpacity
                      onPress={() => field.onChange(option.value)}
                      shouldDebounce={false}
                      disabled={isDisabled ?? option.isDisabled}
                      className={cn(
                        "flex flex-row items-center gap-x-3 rounded-md",
                        Platform.select({
                          web: "cursor-pointer",
                        }),
                        {
                          "items-start": Boolean(option.descriptionI18nKey),
                        },
                      )}>
                      <VnFormControl>
                        <BaseRadioGroupItem
                          value={option.value}
                          disabled={isDisabled ?? option.isDisabled}
                          className={radioItemClassName}
                        />
                      </VnFormControl>
                      <VnBox className="flex min-w-0 flex-1 shrink flex-col gap-y-1.5">
                        <VnFormLabel
                          isDisabled={isDisabled ?? option.isDisabled}
                          variant={radioItemLabelVariant}
                          className={cn(
                            "leading-none",
                            {
                              "font-normal": Boolean(labelI18nKey),
                            },
                            radioItemLabelClassName,
                          )}>
                          {option.label}
                        </VnFormLabel>
                        {option.descriptionI18nKey && (
                          <VnFormDescription
                            i18nKey={option.descriptionI18nKey}
                            i18nOptions={option.descriptionI18nOptions}
                          />
                        )}
                      </VnBox>
                    </VnTouchableOpacity>
                  </VnFormItem>
                ))}
              </BaseRadioGroup>
            </VnFormControl>
            {descriptionI18nKey && (
              <VnFormDescription
                i18nKey={descriptionI18nKey}
                i18nOptions={descriptionI18nOptions}
              />
            )}
          </VnBox>
          {showError && (
            <VnFormErrorMessage
              i18nKey={errorI18nKey}
              i18nOptions={errorI18nOptions}
            />
          )}
        </VnFormItem>
      )}
    />
  );
};
